// MODELS (owner 2026-10-21; was /modal-file-list "Model File List"): a project's .rvt /
// .nwd / .nwc / .nwf files - downloaded from its Procore and ACC models folders (the
// project card's Procore / ACC settings), or uploaded here - in a Document Control kind
// of list: project picker, filters, versions, download, bulk download (zip), export
// (CSV / Excel), a register (PDF), and Send to Procore / ACC. Downloads and uploads use
// the account each project remembers ("Uploads and downloads run as"). "Export to project" turns
// a stored file into a new commit of the project on the heavy worker (no Revit, no Navisworks).
// A stored version the 3D model does not hold yet (the server's needsExport: never exported, or
// newer than the exported one) shows "New version - not in 3D yet" with a primary "Export to
// project" button, and a banner offers to export all of them (owner 2026-10-25). Exports are
// never started by a sync: after one, a notice names the new versions and offers the button.
// "Viewpoints to issues" (a Navisworks file) raises one triage issue per chosen saved viewpoint
// on a target project (ViewpointIssuesModal).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ExportUnderName, ExportHistoryDrawer, ExportLogPanel } from "./ExportProgress";
import { Link } from "react-router-dom";
import { Table, Select, Input, Dropdown, Tag, Tooltip, Modal, Progress, Popover, Button, Space, Empty, notification, Drawer } from "antd";
import {
  DownloadOutlined, DeleteOutlined, ReloadOutlined, UploadOutlined, BuildOutlined, CameraOutlined, SwapOutlined, DownOutlined, FileTextOutlined, CloudUploadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { url, tokenRef } from "api";
import addNotification from "helpers/notify";
import { setSidebarStatus } from "actions/user";
import useLastProject from "components/Coordination/useLastProject";
import { AutodeskLogo, ProcoreLogo } from "components/Integrations/ProviderLogos";
import {
  listModels, syncModels, sendModels, publishModels, retryModel, removeModel, downloadModel, downloadModelsZip, exportModels, modelsRegister, uploadModels,
  exportActivity,
} from "api/projectModels";
import "components/Integrations/integrations.scss";
import "./models.scss";
import ViewpointIssuesModal from "./ViewpointIssuesModal";
import ModelSyncModal from "./ModelSyncModal";
import { getProjectIntegrations } from "components/Integrations/integrationsApi";
import ModelsConnectionBar from "components/Integrations/ModelsConnectionBar";
import { offerFolderPaths } from "components/Integrations/FolderPathsPanel";
import { matchOption } from "components/SearchSelect/searchSelect";

const FORMATS = ["rvt", "nwd", "nwc", "nwf"];
const NAVIS = ["nwd", "nwc", "nwf"];
const SOURCES = [
  { value: "procore", label: "Procore" },
  { value: "acc", label: "ACC" },
  { value: "upload", label: "Uploaded" },
];
const STATUSES = [
  { value: "stored", label: "Downloaded" },
  { value: "queued", label: "Waiting" },
  { value: "downloading", label: "Downloading" },
  { value: "failed", label: "Failed" },
];
const mb = (b) => (b ? `${(b / 1048576).toFixed(b < 10485760 ? 1 : 0)} MB` : "");
const when = (d) => (d ? moment(d).format("YYYY-MM-DD HH:mm") : "");

function SourceCell({ r }) {
  const Logo = r.source === "procore" ? ProcoreLogo : r.source === "acc" ? AutodeskLogo : null;
  return (
    <span className="models-source">
      {Logo ? <Logo connected size={16} /> : <UploadOutlined />}
      <span>
        {r.sourceName}
        {r.remoteVersion ? <small className="text-muted"> v{r.remoteVersion}</small> : null}
        {r.path ? <small className="d-block text-muted models-path" title={r.path}>{r.path}</small> : null}
      </span>
    </span>
  );
}

// one tag per destination (owner 2026-10-24: several per platform); a list from a server
// without destinations shows one per platform, as before
function SentCell({ sent, sentTo }) {
  if (Array.isArray(sentTo) && sentTo.length) {
    return (
      <>
        {sentTo.map((s) => (
          <Tooltip key={s.destination || s.provider} title={s.error || s.path}>
            <Tag color={s.status === "done" ? "green" : s.status === "failed" ? "red" : "blue"}>
              {s.name} v{s.version} {s.status === "done" ? "sent" : s.status}
            </Tag>
          </Tooltip>
        ))}
      </>
    );
  }
  const one = (key, name) => {
    const s = sent && sent[key];
    if (!s) return null;
    const color = s.status === "done" ? "green" : s.status === "failed" ? "red" : "blue";
    return (
      <Tooltip key={key} title={s.error || s.path}>
        <Tag color={color}>
          {name} v{s.version} {s.status === "done" ? "sent" : s.status}
        </Tag>
      </Tooltip>
    );
  };
  return <>{one("procore", "Procore")}{one("acc", "ACC")}</>;
}

const STAGE = { prepare: "Preparing", bank: "Reading the model", publish: "Building 3D" };
const eta = (s) => (s > 90 ? `~${Math.round(s / 60)} min left` : s ? `~${s} s left` : "");

const exporting = (r) => ["queued", "running"].includes(r && r.export && r.export.status);
// a stored version the 3D model does not hold yet, and no export of it under way
const awaitsExport = (r) => !!(r && r.needsExport && !exporting(r));

// `blocked` is a REASON, not a flag: an export is already running on this project, and the
// button has to say so rather than just go grey. This is the second way into publish - the
// first is the BuildOutlined icon in the actions column - and both have to honour the same
// one-at-a-time rule, or the guard is only as good as which button someone happens to press.
function ExportCell({ r, onExport, disabled, blocked }) {
  const x = r.export;
  if (awaitsExport(r)) {
    const failed = x && x.status === "failed";
    return (
      <span className="d-block models-new-version" data-testid={`needs-export-${r._id}`}>
        <Tooltip title={[failed && `Last export failed: ${x.error}`, r.exportedVersion != null && `The 3D model has v${r.exportedVersion}`].filter(Boolean).join(" - ")}>
          <Tag color="gold">New version - not in 3D yet</Tag>
        </Tooltip>
        <Tooltip title={blocked || ""}>
          <button className="header-btn text-white bg-bid border-bid px-2 mt-1 models-export-btn" disabled={disabled || !!blocked}
            data-testid={`export-to-project-${r._id}`} onClick={() => onExport([r._id])}>
            Export to project
          </button>
        </Tooltip>
      </span>
    );
  }
  if (!x) {
    // in the 3D model as a Revit link / NWF member of another file's export
    return r.exportedVersion != null ? (
      <Tooltip title="Exported with another file of the project">
        <Tag color="green">In 3D</Tag>
      </Tooltip>
    ) : null;
  }
  if (x.status === "queued") return <Tag color="blue">Export queued</Tag>;
  if (x.status === "running") {
    return (
      <Tooltip title={eta(x.etaSeconds)}>
        <span className="d-block">
          <small className="text-muted">{STAGE[x.stage] || "Exporting"}</small>
          <Progress percent={x.progress} size="small" />
        </span>
      </Tooltip>
    );
  }
  if (x.status === "failed") {
    return (
      <Tooltip title={x.error}>
        <Tag color="red">Export failed</Tag>
      </Tooltip>
    );
  }
  const missing = (x.parts || []).filter((p) => !p.found);
  const others = (x.composedFrom || []).filter((p) => !p.current).map((p) => p.name);
  return (
    <Tooltip title={[x.exportVersion && `Revision ${x.exportVersion}`, others.length && `With ${others.join(", ")}`, ...(x.warnings || [])].filter(Boolean).join(" - ")}>
      <span data-testid={`in-3d-${r._id}`}>
        <Tag color={missing.length ? "gold" : "green"}>In 3D</Tag>
        <small className="d-block text-muted">{when(x.at)}{missing.length ? ` - ${missing.length} missing` : ""}</small>
      </span>
    </Tooltip>
  );
}

const VP_STAGE = { prepare: "Preparing", render: "Picturing viewpoints", issues: "Creating issues" };

function VpIssuesCell({ x, projects }) {
  if (!x) return null;
  const target = (projects.find((p) => p._id === x.targetProjectId) || {}).title || "";
  if (x.status === "queued") return <Tag color="blue">Issues queued</Tag>;
  if (x.status === "running") {
    return (
      <Tooltip title={[target && `To ${target}`, eta(x.etaSeconds)].filter(Boolean).join(" - ")}>
        <span className="d-block">
          <small className="text-muted">{VP_STAGE[x.stage] || "Working"}</small>
          <Progress percent={x.progress} size="small" />
        </span>
      </Tooltip>
    );
  }
  const tip = [target && `To ${target} (Review Center triage)`, x.error, ...(x.warnings || []),
    ...(x.results || []).filter((r) => r.outcome === "failed").map((r) => `${r.name || r.key}: ${r.error}`)].filter(Boolean).join(" - ");
  if (x.status === "failed") {
    return (
      <Tooltip title={tip}>
        <span>
          <Tag color="red">Issues failed</Tag>
          {x.created ? <small className="d-block text-muted">{x.created} created</small> : null}
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={tip}>
      <span>
        <Tag color={x.failed ? "gold" : "green"}>{x.created} created</Tag>
        <small className="d-block text-muted">
          {[x.skipped ? `${x.skipped} already there` : "", x.failed ? `${x.failed} failed` : ""].filter(Boolean).join(", ") || when(x.at)}
        </small>
      </span>
    </Tooltip>
  );
}

export default function ModelsPage() {
  const dispatch = useDispatch();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useLastProject(projects);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [formats, setFormats] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState("");
  const [syncOpen, setSyncOpen] = useState(false);
  // "only files with a new version not in 3D yet"
  const [onlyNew, setOnlyNew] = useState(false);
  // files a sync started downloading: once in, a notice offers "Export to project"
  const downloads = useRef(new Map());
  // the export each file had at the last load, to say when one finishes
  const lastExports = useRef(new Map());
  // the project's ACC / Procore connections (the header bar) and destinations ("Send to")
  const [integ, setInteg] = useState(null);
  const dests = (integ && integ.destinations) || [];
  const [vpFile, setVpFile] = useState(null);
  // WHAT IS EXPORTING ON THIS PROJECT RIGHT NOW. Its own small read, polled on its own
  // rhythm: the list is heavy and filtered, this is a number and a stage, and the Export
  // button has to know within a second or two rather than at the next list refresh.
  const [activity, setActivity] = useState({ busy: false, percent: 0, runs: [], files: [] });
  // the right drawer: one file's whole export history
  const [historyFile, setHistoryFile] = useState(null);
  // the left drawer: the log of what is running, which the project card links straight to
  const [activeOpen, setActiveOpen] = useState(false);
  // ONE SENTENCE FOR BOTH EXPORT BUTTONS, empty when nothing is running. There are two ways
  // into publish on this page and they must refuse for the same stated reason, otherwise the
  // rule reads as a glitch on whichever button someone happens to press.
  const exportBlockedReason = activity.busy
    ? `An export is already running on this project${activity.runs[0]?.fileName ? ` (${activity.runs[0].fileName})` : ""} - one at a time`
    : "";
  const fileInput = useRef(null);

  useEffect(() => {
    let alive = true;
    fetch(`${url}/projects/picker`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } })
      .then((r) => r.json())
      .then((list) => alive && setProjects(Array.isArray(list) ? list : list?.projects || []))
      .catch(() => alive && setProjects([]));
    return () => {
      alive = false;
    };
  }, []);

  const filters = useMemo(() => ({ search: search.trim(), ext: formats, source: sources, status: statuses }), [search, formats, sources, statuses]);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      setRows(await listModels(projectId, filters));
      setError("");
    } catch (e) {
      setError(e.message);
      setRows([]);
    }
  }, [projectId, filters]);

  useEffect(() => {
    setRows(null);
    setSelected([]);
    setInteg(null);
    setOnlyNew(false);
    downloads.current = new Map();
    lastExports.current = new Map();
    if (!projectId) return;
    getProjectIntegrations(projectId).then((d) => setInteg(d || null)).catch(() => setInteg(null));
  }, [projectId]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  // while something is downloading or being sent, keep the list moving
  const moving = (rows || []).some((r) => r.status === "queued" || r.status === "downloading"
    || ["queued", "running"].includes(r.sent?.procore?.status) || ["queued", "running"].includes(r.sent?.acc?.status)
    || (r.sentTo || []).some((s) => ["queued", "running"].includes(s.status))
    || ["queued", "running"].includes(r.export?.status) || ["queued", "running"].includes(r.vpIssues?.status));
  useEffect(() => {
    if (!moving) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [moving, load]);

  // the activity read, polled while anything is live and once whenever the project changes
  const pullActivity = useCallback(async () => {
    if (!projectId) return;
    try {
      setActivity(await exportActivity(projectId));
    } catch (e) {
      // A FAILED POLL KEEPS THE LAST ANSWER rather than blanking it. If the very first
      // poll fails we stay at busy:false, which leaves the button ENABLED - and that is
      // the right way to fail: the server refuses a second export with 409 whatever this
      // page believes, so an unreachable poll costs a clear error message, never a
      // double export. Disabling on an unknown would strand people instead.
    }
  }, [projectId]);
  useEffect(() => { pullActivity(); }, [pullActivity]);
  useEffect(() => {
    if (!activity.busy) return undefined;
    const t = setInterval(pullActivity, 4000);
    return () => clearInterval(t);
  }, [activity.busy, pullActivity]);

  // ARRIVING FROM A PROJECT CARD: /models?project=<id>&export=active opens the left
  // drawer on whatever is running, which is what the card's "View info" button links to.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("export") === "active") setActiveOpen(true);
    const f = q.get("file");
    if (f && rows) {
      const hit = rows.find((r) => r._id === f);
      if (hit) setHistoryFile(hit);
    }
  }, [rows]);

  const run = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setBusy("");
    }
  };

  // remember what a sync started downloading ({ id, name, version? })
  const watchDownloads = (files) => {
    (files || []).forEach((f) => {
      if (!f || !f.id) return;
      const now = (rows || []).find((r) => r._id === f.id);
      downloads.current.set(f.id, { name: f.name, version: f.version || ((now && now.version) || 0) + 1 });
    });
  };
  const names = (files) => {
    const list = files.slice(0, 5).map((f) => (f.version > 1 ? `${f.name} v${f.version}` : f.name));
    return `${list.join(", ")}${files.length > 5 ? ` and ${files.length - 5} more` : ""}`;
  };

  // "Sync with cloud" downloads: named now, offered for export once they are in
  const syncedDownloads = (out) => {
    const files = (Array.isArray(out) ? out : []).filter((o) => o && o.queued && o.action === "download" && o.id).map((o) => ({ id: o.id, name: o.name }));
    if (!files.length) return;
    watchDownloads(files);
    addNotification(`New versions downloading: ${names(files)} - you can export them to the project once they are downloaded`, "success");
  };

  const sync = (provider) =>
    run(`sync-${provider || "all"}`, async () => {
      const out = await syncModels(projectId, provider);
      out.forEach((o) => {
        const name = o.provider === "acc" ? "ACC" : "Procore";
        if (o.skipped) addNotification(`${name}: ${o.skipped}`, "warning");
        else if (o.error) addNotification(`${name}: ${o.error}`, "danger");
        else {
          const files = o.files || [];
          watchDownloads(files);
          addNotification(
            `${name}: ${o.found} model file${o.found === 1 ? "" : "s"} found, ${o.queued} downloading, ${o.unchanged} already up to date`
              + (files.length ? `. New versions: ${names(files)} - you can export them to the project once they are downloaded` : ""),
            "success",
          );
        }
      });
      // NO MODELS DOWNLOAD FOLDER ANYWHERE: said, with the way to set one (owner 2026-10-25)
      const needs = (Array.isArray(out) ? out : []).filter((o) => o && o.needsPath);
      if (needs.length && needs.length === out.length) {
        offerFolderPaths(projectId, "No models download folder is set for this project's Procore or ACC accounts, so there is nowhere to get the latest models from. Set one - a default folder, or one per type (.rvt, .nwc, .nwd).");
      }
      load();
    });

  // `key`: a destination id, or a platform when the project lists no destinations
  const send = (key) =>
    run(`send-${key}`, async () => {
      const d = dests.find((x) => x.destination === key);
      const out = await sendModels(projectId, d ? [key] : key, selected);
      const queued = out.filter((o) => o.queued).length;
      const skipped = out.filter((o) => o.skipped);
      const where = d ? d.name : key === "acc" ? "ACC" : "Procore";
      if (queued) addNotification(`${queued} file${queued === 1 ? "" : "s"} on the way to ${where}`, "success");
      skipped.slice(0, 5).forEach((o) => addNotification(`${o.name}: ${o.skipped}`, "warning"));
      load();
    });

  const publish = (ids) =>
    run("publish", async () => {
      const out = await publishModels(projectId, ids);
      const queued = out.filter((o) => o.queued).length;
      if (queued) addNotification(`${queued} file${queued === 1 ? "" : "s"} exporting to the project - each one becomes a new revision of its 3D model`, "success");
      out.filter((o) => o.skipped).slice(0, 5).forEach((o) => addNotification(`${o.name || o.id}: ${o.skipped}`, "warning"));
      load();
    });
  const publishRef = useRef(publish);
  publishRef.current = publish;

  // after each load: downloads a sync started that are now in, and exports that finished
  useEffect(() => {
    if (!rows) return;
    const arrived = [];
    rows.forEach((r) => {
      const want = downloads.current.get(r._id);
      if (!want) return;
      if (r.status === "failed") downloads.current.delete(r._id);
      else if (r.status === "stored" && (r.version || 0) >= want.version) {
        downloads.current.delete(r._id);
        if (awaitsExport(r)) arrived.push(r);
      }
    });
    if (arrived.length) {
      const key = `models-new-${arrived.map((r) => r._id).join("-")}`;
      const ids = arrived.map((r) => r._id);
      notification.open({
        key,
        duration: 0,
        message: `New version${arrived.length === 1 ? "" : "s"} downloaded - not in 3D yet`,
        description: names(arrived),
        btn: (
          <button className="header-btn text-white bg-bid border-bid px-3" data-testid="notice-export-to-project"
            onClick={() => { notification.destroy(key); publishRef.current(ids); }}>
            Export to project
          </button>
        ),
      });
    }
    rows.forEach((r) => {
      const before = lastExports.current.get(r._id);
      const x = r.export;
      if (before && x && before.id === x.id && ["queued", "running"].includes(before.status) && x.status === "done") {
        notification.success({
          key: `models-done-${x.id}`,
          message: `${r.name} is in the project's 3D model`,
          description: x.exportVersion ? `Revision ${x.exportVersion}` : "",
          btn: <a className="integration-link-btn" href={`/personal-area/projects/model-view/${projectId}`}>Open 3D model</a>,
        });
      }
    });
    lastExports.current = new Map(rows.map((r) => [r._id, r.export ? { id: r.export.id, status: r.export.status } : null]));
  }, [rows, projectId]);

  const upload = (files) =>
    run("upload", async () => {
      if (!files || !files.length) return;
      const out = await uploadModels(projectId, files);
      addNotification(`${out.length} model file${out.length === 1 ? "" : "s"} uploaded`, "success");
      load();
    });

  const remove = (r) =>
    Modal.confirm({
      title: `Take "${r.name}" off this project's models?`,
      content: r.source === "upload" ? "The uploaded copy is no longer listed." : `Nothing changes on ${r.sourceName}; the file comes back here only when it gets a new version there.`,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: () => run("remove", async () => { await removeModel(projectId, r._id); load(); }),
    });

  const columns = [
    {
      title: "File name",
      dataIndex: "name",
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }),
      render: (name, r) => (
        <div>
          <b
            className="models-name"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            title="Export history"
            onClick={() => setHistoryFile(r)}
            onKeyDown={(e) => { if (e.key === "Enter") setHistoryFile(r); }}
          >
            {name}
          </b>
          {/* UNDER THE NAME, where the owner asked for it: stage, percent, time left,
              and the log behind an expand. Silent for a file nothing has happened to. */}
          <ExportUnderName projectId={projectId} x={r.export} onOpenHistory={() => setHistoryFile(r)} />
        </div>
      ),
    },
    { title: "Format", dataIndex: "ext", width: 64, render: (e) => <Tag>{String(e).toUpperCase()}</Tag>, sorter: (a, b) => a.ext.localeCompare(b.ext) },
    {
      title: "V",
      dataIndex: "version",
      width: 48,
      sorter: (a, b) => a.version - b.version,
      render: (v, r) =>
        r.versions && r.versions.length > 1 ? (
          <Popover
            title="Versions"
            content={
              <div>
                {[...r.versions].reverse().map((x) => (
                  <div key={x.version} className="d-flex align-items-center" style={{ gap: 8 }}>
                    <span>v{x.version}{x.remoteVersion ? ` (source v${x.remoteVersion})` : ""}</span>
                    <small className="text-muted">{when(x.at)} - {mb(x.bytes)}</small>
                    <button className="integration-link-btn" onClick={() => run("dl", () => downloadModel(projectId, r._id, r.name, x.version))}>
                      Download
                    </button>
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="blue" style={{ cursor: "pointer" }}>v{v}</Tag>
          </Popover>
        ) : v ? `v${v}` : "",
    },
    { title: "Source", dataIndex: "source", width: 190, render: (s, r) => <SourceCell r={r} />, sorter: (a, b) => a.source.localeCompare(b.source) },
    { title: "Size", dataIndex: "bytes", width: 72, render: (b, r) => mb(b || r.remoteSize), sorter: (a, b) => (a.bytes || a.remoteSize) - (b.bytes || b.remoteSize) },
    { title: "Modified at source", dataIndex: "remoteModifiedAt", width: 96, render: when, sorter: (a, b) => new Date(a.remoteModifiedAt || 0) - new Date(b.remoteModifiedAt || 0) },
    {
      title: "Imported",
      dataIndex: "importedAt",
      width: 112,
      defaultSortOrder: "descend",
      sorter: (a, b) => new Date(a.importedAt || 0) - new Date(b.importedAt || 0),
      render: (d, r) => (
        <span>
          {when(d)}
          {r.importedBy ? <small className="d-block text-muted">{r.importedBy}</small> : null}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 104,
      render: (s, r) =>
        s === "stored" ? (
          <Tag color="green">Downloaded</Tag>
        ) : s === "downloading" ? (
          <Progress percent={r.progress} size="small" />
        ) : s === "queued" ? (
          <Tag color="blue">Waiting</Tag>
        ) : (
          <span>
            <Tooltip title={r.error}>
              <Tag color="red">Failed</Tag>
            </Tooltip>
            <button className="integration-link-btn" onClick={() => run("retry", async () => { await retryModel(projectId, r._id); load(); })}>
              Retry
            </button>
          </span>
        ),
    },
    { title: "3D model", key: "export", width: 160, render: (x, r) => <ExportCell r={r} onExport={publish} disabled={!!busy} blocked={exportBlockedReason} /> },
    { title: "Issues", key: "vpIssues", width: 100, render: (x, r) => <VpIssuesCell x={r.vpIssues} projects={projects} /> },
    { title: "Sent to", key: "sent", width: 120, render: (x, r) => <SentCell sent={r.sent} sentTo={r.sentTo} /> },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (x, r) => (
        <span className="d-inline-flex" style={{ gap: 8 }}>
          {NAVIS.includes(r.ext) ? (
            <Tooltip title={r.status === "stored" ? "Viewpoints to issues" : "Not downloaded yet"}>
              <button className="models-icon" aria-label="Viewpoints to issues" disabled={!!busy || r.status !== "stored" || ["queued", "running"].includes(r.vpIssues?.status)}
                onClick={() => setVpFile(r)}>
                <CameraOutlined />
              </button>
            </Tooltip>
          ) : null}
          {awaitsExport(r) ? null : (
            <Tooltip title={exportBlockedReason
              || (r.status === "stored" ? "Export to project (a new revision of its 3D model)" : "Not downloaded yet")}>
              <button className="models-icon" aria-label="Export to project"
                disabled={!!busy || !!exportBlockedReason || r.status !== "stored" || exporting(r)}
                onClick={() => publish([r._id])}>
                <BuildOutlined />
              </button>
            </Tooltip>
          )}
          <Tooltip title={r.status === "stored" ? "Download" : "Not downloaded yet"}>
            <button className="models-icon" disabled={r.status !== "stored"} onClick={() => run("dl", () => downloadModel(projectId, r._id, r.name))}>
              <DownloadOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Take off this list">
            <button className="models-icon" onClick={() => remove(r)}>
              <DeleteOutlined />
            </button>
          </Tooltip>
        </span>
      ),
    },
  ];

  const newVersions = (rows || []).filter(awaitsExport);
  const shown = onlyNew && newVersions.length ? newVersions : rows || [];

  const exportMenu = {
    items: [
      { key: "csv", label: "CSV" },
      { key: "xlsx", label: "Excel" },
    ],
    onClick: ({ key }) => run("export", () => exportModels(projectId, filters, key)),
  };
  const syncMenu = {
    items: [
      { key: "procore", label: "From Procore" },
      { key: "acc", label: "From ACC" },
    ],
    onClick: ({ key }) => sync(key),
  };
  const sendMenu = {
    items: dests.length
      ? dests.map((d) => ({ key: d.destination, label: `To ${d.name}` }))
      : [
        { key: "procore", label: "To Procore" },
        { key: "acc", label: "To ACC" },
      ],
    onClick: ({ key }) => send(key),
  };

  const count = shown.length;
  const anyFilter = !!(filters.search || formats.length || sources.length || statuses.length || onlyNew);

  // LAID OUT LIKE SHOP DRAWINGS (owner 2026-10-25): the page's full width, one row of
  // project + actions, the Procore / ACC bar, the filters with the count, then the table.
  return (
    <div className="models-page">
      <div className="models-title">
        <span onClick={() => dispatch(setSidebarStatus(true))} className="cursor-pointer models-back" title="Menu">
          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="10" viewBox="0 0 7 10" fill="none">
            <path d="M6.84102 8.825L3.02435 5L6.84102 1.175L5.66602 0L0.666016 5L5.66602 10L6.84102 8.825Z" fill="#9E9E9E" />
          </svg>
        </span>
        <span>Models</span>
        <span className="models-spacer" />
        <Link to="/models/health-check-files" className="models-link">
          Health check files
        </Link>
      </div>
      <Space className="models-actions" wrap>
        <Select filterOption={matchOption}
          showSearch
          optionFilterProp="label"
          placeholder="Select a project"
          style={{ minWidth: 280 }}
          value={projectId || undefined}
          onChange={setProjectId}
          options={projects.map((p) => ({ value: p._id, label: p.title }))}
        />
        {projectId ? (
          <>
            <Dropdown.Button type="primary" menu={syncMenu} disabled={!!busy} onClick={() => sync()} icon={<DownOutlined />}>
              <ReloadOutlined /> {busy.startsWith("sync") ? "Checking..." : "Get latest models"}
            </Dropdown.Button>
            <Button icon={<SwapOutlined />} disabled={!!busy} onClick={() => setSyncOpen(true)} data-testid="models-sync-cloud">
              Sync with cloud...
            </Button>
            <Button icon={<UploadOutlined />} disabled={!!busy} onClick={() => fileInput.current && fileInput.current.click()}>
              Upload
            </Button>
            <input ref={fileInput} type="file" multiple accept=".rvt,.nwd,.nwc,.nwf" style={{ display: "none" }}
              onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
            <Dropdown trigger={["click"]} menu={exportMenu} disabled={!!busy || !rows || !rows.length}>
              <Button icon={<DownloadOutlined />} loading={busy === "export"}>
                Export <DownOutlined />
              </Button>
            </Dropdown>
            <Button icon={<FileTextOutlined />} loading={busy === "register"} disabled={!!busy || !rows || !rows.length}
              onClick={() => run("register", () => modelsRegister(projectId, filters))}>
              Register
            </Button>
          </>
        ) : null}
      </Space>

      {projectId ? <ModelsConnectionBar data={integ} projectId={projectId} /> : null}

      {!projectId ? (
        <Empty description="Pick a project to see its model files" />
      ) : (
        <>
          <div className="models-toolbar">
            <Input.Search allowClear placeholder="Search name or path" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} />
            <Select mode="multiple" allowClear placeholder="Format" style={{ minWidth: 140 }} value={formats} onChange={setFormats}
              options={FORMATS.map((f) => ({ value: f, label: f.toUpperCase() }))} />
            <Select mode="multiple" allowClear placeholder="Source" style={{ minWidth: 140 }} value={sources} onChange={setSources} options={SOURCES} />
            <Select mode="multiple" allowClear placeholder="Status" style={{ minWidth: 140 }} value={statuses} onChange={setStatuses} options={STATUSES} />
            <span className="models-spacer" />
            <small className="text-muted" data-testid="models-count">
              {rows === null ? "…" : `${count.toLocaleString()} ${count === 1 ? "file" : "files"}${anyFilter ? " match" : ""}`}
            </small>
          </div>
          {selected.length ? (
            <div className="models-selection">
              <b>{selected.length} selected</b>
              <Button size="small" icon={<DownloadOutlined />} disabled={!!busy}
                onClick={() => run("zip", () => downloadModelsZip(projectId, selected))}>
                {busy === "zip" ? "Preparing zip..." : "Download"}
              </Button>
              <Button size="small" icon={<BuildOutlined />} disabled={!!busy} onClick={() => publish(selected)}>
                Export to project
              </Button>
              <Dropdown trigger={["click"]} menu={sendMenu} disabled={!!busy}>
                <Button size="small" icon={<CloudUploadOutlined />}>
                  Send to Procore / ACC <DownOutlined />
                </Button>
              </Dropdown>
              <button className="integration-link-btn" onClick={() => setSelected([])}>Clear</button>
            </div>
          ) : null}
          {newVersions.length ? (
            <div className="models-new-banner" data-testid="models-new-banner">
              <span>
                <b>{newVersions.length} file{newVersions.length === 1 ? " has a new version" : "s have new versions"} not yet exported</b>
                {" - "}
                <button className="integration-link-btn" data-testid="models-only-new" onClick={() => setOnlyNew((v) => !v)}>
                  {onlyNew ? "Show all files" : "Show only these"}
                </button>
              </span>
              <Button type="primary" size="small" icon={<BuildOutlined />} disabled={!!busy} data-testid="models-export-all"
                onClick={() => publish(newVersions.map((r) => r._id))}>
                Export all to project
              </Button>
            </div>
          ) : null}
          {error ? <p className="text-danger">{error}</p> : null}
          <Table
            className="models-table"
            rowKey="_id"
            size="small"
            loading={rows === null}
            dataSource={shown}
            columns={columns}
            rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
            pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
            // the page's width; below 1,360 px the table scrolls rather than squeezing names
            scroll={{ x: 1360 }}
            locale={{
              emptyText: (
                <div className="text-muted py-3">
                  No model files yet. Set the project&apos;s models folder in its Procore or ACC settings (the project card), then
                  &quot;Get latest models&quot; - or upload .rvt / .nwd / .nwc / .nwf files here.
                </div>
              ),
            }}
          />
          <ModelSyncModal open={syncOpen} projectId={projectId} onClose={() => setSyncOpen(false)} onDone={(out) => { syncedDownloads(out); load(); }} />
          {/* RIGHT: one file's whole export history. Opens on a click of the name. */}
          <ExportHistoryDrawer
            projectId={projectId}
            file={historyFile}
            open={!!historyFile}
            onClose={() => setHistoryFile(null)}
          />

          {/* LEFT: what is running now, with its live log. This is where the project
              card's "View info" button lands (/models?project=<id>&export=active). */}
          <Drawer
            title="Export in progress"
            placement="left"
            width={480}
            open={activeOpen}
            onClose={() => setActiveOpen(false)}
            destroyOnClose
          >
            {activity.busy ? (
              activity.runs.map((r) => (
                <div key={r.id} className="mb-3">
                  <b>{r.fileName}</b>
                  <small className="d-block text-muted">
                    {STAGE[r.stage] || r.stage || "Working"}
                    {r.by ? ` - started by ${r.by}` : ""}
                    {r.etaSeconds ? ` - ${eta(r.etaSeconds)}` : ""}
                  </small>
                  <Progress percent={r.progress} status="active" />
                  <ExportLogPanel projectId={projectId} runId={r.id} live height={320} />
                </div>
              ))
            ) : (
              <div className="text-muted">
                Nothing is exporting on this project right now.
              </div>
            )}
          </Drawer>
          <ViewpointIssuesModal
            open={!!vpFile}
            projectId={projectId}
            file={vpFile}
            projects={projects}
            onClose={() => setVpFile(null)}
            onQueued={() => load()}
          />
        </>
      )}
    </div>
  );
}
